import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { resolvePublicFileUrlForImg } from '@/config/apiBaseUrl';
import { NEWBASE_CERTIFICATE_LOGO_PATH, MEDICREW_CERTIFICATE_FOOTER_LOGO_PATH } from '@/lib/certificateIssuerAssets';

export interface ClassCertificateLabels {
	title: string;
	affiliation: string;
	studentName: string;
	courseName: string;
	educationInstitution: string;
	period: string;
	issueDate: string;
}

export interface ClassCertificateData {
	documentNumber: string;
	studentName: string;
	affiliationName: string;
	courseName: string;
	educationInstitutionName: string;
	participationPeriodText: string;
	issueDateText: string;
	/** 파트너 기관 서명 이미지 (API 미제공 시 undefined) */
	issuerSignatureImageUrl?: string | null;
	/** 파트너기관 로고 URL (API 또는 정적 asset) */
	issuerLogoImageUrl?: string | null;
	/** 서명 하단 — 파트너기관 로고 URL (정적 asset) */
	partnerLogoImageUrl?: string | null;
	labels: ClassCertificateLabels;
}

const CERTIFICATE_FONT_STACK =
	"-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Pretendard', 'Noto Sans KR', sans-serif";

/** A4 @ 96dpi — html2canvas 캡처 기준 크기 */
const CAPTURE_WIDTH_PX = 794;
const CAPTURE_MIN_HEIGHT_PX = 1123;
const CERTIFICATE_INFO_SECTION_MAX_WIDTH_PX = 520;

/** 인쇄·PDF 캡처 시 CMYK 변환 후에도 단색감이 유지되는 브랜드 blue */
const CERTIFICATE_PRINT_PRIMARY = '#1a4480';

const CERTIFICATE_COLORS = {
	primary: CERTIFICATE_PRINT_PRIMARY,
	light: '#93c5fd',
	footerBackground: CERTIFICATE_PRINT_PRIMARY,
	gradientTop: '#dbeafe',
	gradientMid: '#eff6ff',
	gradientBottom: '#ffffff',
	text: '#111827',
	muted: '#6b7280',
	surface: '#ffffff',
	border: '#e5e7eb',
} as const;

/** 페이지 높이 대비 배경 그라데이션 종료 지점 (상단 2/3) */
const CERTIFICATE_BACKGROUND_GRADIENT_END = '66%';

const CERTIFICATE_BACKGROUND_GRADIENT = `linear-gradient(to bottom, ${CERTIFICATE_COLORS.gradientTop} 0%, ${CERTIFICATE_COLORS.gradientMid} 38%, ${CERTIFICATE_COLORS.gradientBottom} ${CERTIFICATE_BACKGROUND_GRADIENT_END}, ${CERTIFICATE_COLORS.gradientBottom} 100%)`;

/** PDF 파일명에 사용할 수 없는 문자 제거 */
export function sanitizeCertificateFileNameSegment(value: string): string {
	return value
		.trim()
		.replace(/[/\\?%*:|"<>]/g, '_')
		.replace(/\s+/g, '_')
		.slice(0, 80) || 'certificate';
}

export function buildClassCertificateFileName(
	studentName: string,
	classTitle: string,
	issueDateYmd: string
): string {
	const safeStudent = sanitizeCertificateFileNameSegment(studentName);
	const safeClass = sanitizeCertificateFileNameSegment(classTitle);
	return `${safeStudent}_${safeClass}_이수증_${issueDateYmd}.pdf`;
}

function appendCertificateInfoRow(
	parent: HTMLElement,
	label: string,
	value: string,
	isLastRow: boolean
): void {
	const row = document.createElement('div');
	row.style.cssText = [
		'display: flex',
		'align-items: flex-start',
		'gap: 20px',
		'padding: 13px 24px',
		'width: 100%',
		'box-sizing: border-box',
		isLastRow ? '' : `border-bottom: 1px solid ${CERTIFICATE_COLORS.border}`,
	].join(';');

	const labelElement = document.createElement('span');
	labelElement.textContent = label;
	labelElement.style.cssText = `width: 108px; flex-shrink: 0; font-size: 14px; font-weight: 600; color: ${CERTIFICATE_COLORS.primary}; line-height: 1.5; word-break: keep-all;`;

	const valueElement = document.createElement('span');
	valueElement.textContent = value || '—';
	valueElement.style.cssText = [
		'flex: 1',
		'min-width: 0',
		'font-size: 16px',
		'font-weight: 500',
		`color: ${CERTIFICATE_COLORS.text}`,
		'line-height: 1.5',
		'white-space: normal',
		'overflow-wrap: break-word',
		'word-break: break-word',
	].join(';');

	row.appendChild(labelElement);
	row.appendChild(valueElement);
	parent.appendChild(row);
}

function resolveImageUrl(rawUrl: string | null | undefined): string | null {
	const trimmedUrl = rawUrl?.trim();
	if (!trimmedUrl) return null;
	
	// public 폴더의 정적 파일 (/assets/로 시작)은 그대로 사용
	if (trimmedUrl.startsWith('/assets/') || trimmedUrl.startsWith('/')) {
		return trimmedUrl;
	}
	
	// 외부 URL이나 API 경로는 resolvePublicFileUrlForImg로 변환
	return resolvePublicFileUrlForImg(trimmedUrl) ?? trimmedUrl;
}

function preloadImage(imageUrl: string): Promise<boolean> {
	return new Promise((resolve) => {
		const image = new Image();
		image.crossOrigin = 'anonymous';
		image.onload = () => resolve(true);
		image.onerror = () => resolve(false);
		image.src = imageUrl;
	});
}

async function resolveLoadableImageUrl(rawUrl: string | null | undefined): Promise<string | null> {
	const imageUrl = resolveImageUrl(rawUrl);
	if (!imageUrl) return null;
	return (await preloadImage(imageUrl)) ? imageUrl : null;
}

/** 파트너기관 서명만 반환 (없으면 null) */
export async function resolveIssuerDisplayImageUrl(data: ClassCertificateData): Promise<string | null> {
	return resolveLoadableImageUrl(data.issuerSignatureImageUrl);
}

export interface CertificateIssuerDisplayImages {
	issuerImageUrl: string | null;
	partnerLogoImageUrl: string | null;
}

/** 서명(없으면 빈칸) + 하단 파트너기관 로고(없으면 NEWBASE) */
export async function resolveCertificateIssuerDisplayImages(
	data: ClassCertificateData
): Promise<CertificateIssuerDisplayImages> {
	const issuerImageUrl = await resolveLoadableImageUrl(data.issuerSignatureImageUrl);
	const partnerLogoImageUrl =
		(await resolveLoadableImageUrl(data.partnerLogoImageUrl)) ??
		(await resolveLoadableImageUrl(data.issuerLogoImageUrl)) ??
		(await resolveLoadableImageUrl(NEWBASE_CERTIFICATE_LOGO_PATH));

	return {
		issuerImageUrl,
		partnerLogoImageUrl,
	};
}

function appendAccentBar(
	parent: HTMLElement,
	options?: { maxWidthPx?: number; margin?: string }
): void {
	const accentBar = document.createElement('div');
	const accentBarStyles = [
		'height: 6px',
		`background: linear-gradient(to right, transparent, ${CERTIFICATE_COLORS.light}, transparent)`,
	];
	if (options?.maxWidthPx) {
		accentBarStyles.push(
			'width: 100%',
			`max-width: ${options.maxWidthPx}px`,
			'margin: 0 auto',
			'align-self: center'
		);
	}
	if (options?.margin) {
		accentBarStyles.push(`margin: ${options.margin}`);
	}
	accentBar.style.cssText = accentBarStyles.join(';');
	parent.appendChild(accentBar);
}

function appendTitleBlock(parent: HTMLElement, titleText: string): void {
	const titleBlock = document.createElement('div');
	titleBlock.style.cssText = [
		'margin: 24px 0 0',
		'padding: 32px 24px 0',
		'text-align: center',
	].join(';');

	const title = document.createElement('h1');
	title.textContent = titleText;
	title.style.cssText = `margin: 0; font-size: 40px; font-weight: 700; color: ${CERTIFICATE_COLORS.primary}; letter-spacing: 0.08em;`;

	titleBlock.appendChild(title);
	parent.appendChild(titleBlock);
}

function appendIssuerSignatureSection(
	parent: HTMLElement,
	data: ClassCertificateData,
	issuerImageUrl: string | null,
	partnerLogoImageUrl: string | null
): void {
	const section = document.createElement('div');
	section.style.cssText = 'margin-top: 48px; text-align: center;';

	if (issuerImageUrl) {
		const imageElement = document.createElement('img');
		imageElement.src = issuerImageUrl;
		imageElement.alt = data.educationInstitutionName;
		imageElement.style.cssText =
			'display: block; margin: 0 auto; max-width: 330px; max-height: 120px; object-fit: contain;';
		section.appendChild(imageElement);
	} else {
		const signaturePlaceholder = document.createElement('div');
		signaturePlaceholder.style.cssText = 'height: 120px;';
		section.appendChild(signaturePlaceholder);
	}

	if (partnerLogoImageUrl) {
		const partnerLogoElement = document.createElement('img');
		partnerLogoElement.src = partnerLogoImageUrl;
		partnerLogoElement.alt =
			partnerLogoImageUrl === NEWBASE_CERTIFICATE_LOGO_PATH ? 'NEWBASE' : data.educationInstitutionName;
		partnerLogoElement.style.cssText =
			'display: block; margin: 28px auto 0; max-width: 180px; max-height: 64px; object-fit: contain;';
		section.appendChild(partnerLogoElement);
	}

	parent.appendChild(section);
}

function appendPoweredByFooter(parent: HTMLElement, isPreview: boolean = false): void {
	const footer = document.createElement('div');
	footer.style.cssText = [
		'box-sizing: border-box',
		'width: 100%',
		`background: ${CERTIFICATE_COLORS.footerBackground}`,
		'-webkit-print-color-adjust: exact',
		'print-color-adjust: exact',
		'padding: 20px 48px',
		'text-align: center',
	].join(';');

	// 텍스트와 로고를 하나의 span에 inline으로 넣기
	const container = document.createElement('span');
	container.style.cssText = 'font-size: 14px; font-weight: 500; color: #ffffff; letter-spacing: 0.02em; line-height: 16px; white-space: nowrap; display: inline-flex; align-items: center; gap: 4px;';
	
	// "Powered by " 텍스트
	const textSpan = document.createElement('span');
	textSpan.textContent = 'Powered by';
	textSpan.style.cssText = 'display: inline-block;';
	container.appendChild(textSpan);

	// medicrew 로고 (inline)
	const footerLogoUrl = resolveImageUrl(MEDICREW_CERTIFICATE_FOOTER_LOGO_PATH) ?? MEDICREW_CERTIFICATE_FOOTER_LOGO_PATH;
	const footerLogo = document.createElement('img');
	footerLogo.src = footerLogoUrl;
	footerLogo.alt = 'medicrew';
	footerLogo.style.cssText = 'display: inline-block; height: 14px; width: auto; object-fit: contain; margin: 0; padding: 0;';
	
	container.appendChild(footerLogo);
	footer.appendChild(container);
	parent.appendChild(footer);
}

/** 화면 밖 HTML 이수증 — html2canvas 캡처용 (한글·CSS 레이아웃) */
export function buildCertificateCaptureElement(
	data: ClassCertificateData,
	issuerImageUrl: string | null,
	partnerLogoImageUrl: string | null = null,
	options?: { isPreview?: boolean }
): HTMLElement {
	const root = document.createElement('div');
	root.setAttribute('data-certificate-capture', 'true');
	
	const baseStyles = [
		`box-sizing: border-box`,
		`background: ${CERTIFICATE_BACKGROUND_GRADIENT}`,
		`font-family: ${CERTIFICATE_FONT_STACK}`,
		`color: ${CERTIFICATE_COLORS.text}`,
		`display: flex`,
		`flex-direction: column`,
		`overflow: hidden`,
	];
	
	if (options?.isPreview) {
		// 미리보기 모드: 화면에 표시, 반응형
		root.style.cssText = [
			...baseStyles,
			`width: 100%`,
			`max-width: 794px`,
			`margin: 0 auto`,
			`aspect-ratio: 794 / 1123`,
			`border-radius: 8px`,
			`box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)`,
		].join(';');
	} else {
		// 캡처 모드: 화면 밖, 고정 크기
		root.style.cssText = [
			...baseStyles,
			`position: fixed`,
			`left: -10000px`,
			`top: 0`,
			`width: ${CAPTURE_WIDTH_PX}px`,
			`height: ${CAPTURE_MIN_HEIGHT_PX}px`,
			`min-height: ${CAPTURE_MIN_HEIGHT_PX}px`,
		].join(';');
	}

	const frame = document.createElement('div');
	frame.style.cssText = [
		'box-sizing: border-box',
		'flex: 1',
		'display: flex',
		'flex-direction: column',
		'min-height: 0',
	].join(';');

	const content = document.createElement('div');
	content.style.cssText =
		'box-sizing: border-box; flex: 1; padding: 48px 48px 40px; display: flex; flex-direction: column;';

	const documentNumberLine = document.createElement('p');
	documentNumberLine.textContent = data.documentNumber;
	documentNumberLine.style.cssText = `margin: 0 0 16px; text-align: right; font-size: 12px; font-weight: 500; color: ${CERTIFICATE_COLORS.text}; letter-spacing: 0.04em;`;
	content.appendChild(documentNumberLine);

	appendTitleBlock(content, data.labels.title);
	appendAccentBar(content, {
		maxWidthPx: CERTIFICATE_INFO_SECTION_MAX_WIDTH_PX,
		margin: '28px auto 32px',
	});

	const infoSection = document.createElement('div');
	infoSection.style.cssText = [
		'flex: 1',
		'width: 100%',
		`max-width: ${CERTIFICATE_INFO_SECTION_MAX_WIDTH_PX}px`,
		'margin: 0 auto',
		'align-self: center',
		'background: #ffffff',
		'overflow: hidden',
	].join(';');

	const infoRows: Array<[string, string]> = [
		[data.labels.affiliation, data.affiliationName],
		[data.labels.studentName, data.studentName],
		[data.labels.courseName, data.courseName],
		[data.labels.educationInstitution, data.educationInstitutionName],
		[data.labels.period, data.participationPeriodText],
		[data.labels.issueDate, data.issueDateText],
	];
	infoRows.forEach(([label, value], rowIndex) => {
		appendCertificateInfoRow(infoSection, label, value, rowIndex === infoRows.length - 1);
	});

	content.appendChild(infoSection);
	appendIssuerSignatureSection(content, data, issuerImageUrl, partnerLogoImageUrl);

	frame.appendChild(content);
	root.appendChild(frame);
	appendPoweredByFooter(root, options?.isPreview ?? false);

	return root;
}

/**
 * 마이클래스 이수증 PDF 생성 및 다운로드 (A4 세로, HTML → html2canvas → jsPDF)
 */
export async function generateClassCertificatePdf(data: ClassCertificateData): Promise<void> {
	const { issuerImageUrl, partnerLogoImageUrl } = await resolveCertificateIssuerDisplayImages(data);
	const captureElement = buildCertificateCaptureElement(data, issuerImageUrl, partnerLogoImageUrl);
	document.body.appendChild(captureElement);

	try {
		await new Promise<void>((resolve) => {
			window.setTimeout(resolve, 200);
		});

		const canvas = await html2canvas(captureElement, {
			scale: 2,
			useCORS: true,
			logging: false,
			backgroundColor: null,
			width: CAPTURE_WIDTH_PX,
			height: CAPTURE_MIN_HEIGHT_PX,
			windowWidth: CAPTURE_WIDTH_PX,
			windowHeight: CAPTURE_MIN_HEIGHT_PX,
		});

		const imgData = canvas.toDataURL('image/png');
		const pdf = new jsPDF('p', 'mm', 'a4');
		const pageWidth = pdf.internal.pageSize.getWidth();
		const pageHeight = pdf.internal.pageSize.getHeight();
		pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);

		const fileName = buildClassCertificateFileName(
			data.studentName,
			data.courseName,
			new Date().toISOString().slice(0, 10)
		);
		pdf.save(fileName);
	} finally {
		document.body.removeChild(captureElement);
	}
}
